import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Spinner from "@components/Spinner";
import {
  getAdminFields,
  getAdminSeasons,
  getAdminFarms,
  getFieldYieldAggregates,
  uploadFieldYield,
  AdminField,
  AdminSeason,
  AdminFarm,
  AdminYieldAggregate,
} from "@services/adminService";
import {
  getHeatmapData,
  TLegendItem,
  TStatsItem,
} from "@services/fieldsService";

import OpenLayersMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { TileJSON } from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import { Fill, Stroke, Style } from "ol/style";
import { Feature, MapBrowserEvent } from "ol";
import { Geometry } from "ol/geom";
import { isEmpty as isEmptyExtent } from "ol/extent";
import "ol/ol.css";

const Container = styled.div`
  display: flex;
  gap: 16px;
  height: calc(100vh - 340px);
  min-height: 400px;
`;

const LeftPanel = styled.div`
  width: 360px;
  min-width: 360px;
  overflow-y: auto;
  border: 1px solid #ebeeef;
  border-radius: 8px;
`;

const RightPanel = styled.div`
  flex: 1;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const SeasonRow = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #ebeeef;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SeasonSelect = styled.select`
  height: 32px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  padding: 0 8px;
  flex: 1;
  outline: none;
  &:focus {
    border-color: #68ad18;
  }
`;

const FieldCard = styled.div<{ selected?: boolean }>`
  padding: 12px 16px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  background: ${({ selected }) => (selected ? "#f0f9e8" : "transparent")};
  border-left: 3px solid
    ${({ selected }) => (selected ? "#68ad18" : "transparent")};
  &:hover {
    background: ${({ selected }) => (selected ? "#f0f9e8" : "#fafafa")};
  }
`;

const FieldName = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: bold;
  color: #4a90d9;
  margin-bottom: 4px;
`;

const FieldDetail = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const FieldPreviewWrap = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background: #f8f8f8;
  border-radius: 4px;
  overflow: hidden;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
  width: 100%;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
  width: 100%;
`;

const FieldCount = styled.div`
  padding: 8px 16px;
  border-bottom: 1px solid #ebeeef;
  background: #fafafa;
`;

const UploadButton = styled.button`
  cursor: pointer;
  outline: none;
  height: 28px;
  border-radius: 4px;
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 12px;
  background: #68ad18;
  color: #fff;
  border: none;
  padding: 0 12px;
  white-space: nowrap;
  &:hover {
    box-shadow: inset 0px 0px 5px rgba(0, 0, 0, 0.3);
  }
  &:disabled {
    cursor: not-allowed;
    background: #cbd5d8;
  }
`;

const YieldInfo = styled.div`
  margin-top: 4px;
  padding: 4px 8px;
  background: #f0f9e8;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HiddenInput = styled.input`
  display: none;
`;

const MapOverlay = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
`;

const ViewSelect = styled.select`
  height: 32px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  padding: 0 8px;
  outline: none;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  &:focus {
    border-color: #68ad18;
  }
`;

const LegendBox = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 8px 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  min-width: 120px;
`;

const LegendTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: bold;
  color: #494c5b;
  margin-bottom: 6px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
`;

const LegendSwatch = styled.div<{ color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 2px;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #494c5b;
`;

const StatsRow = styled.div`
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #eee;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #68ad18;
`;

const optOptions = {
  dataProjection: "EPSG:4326",
  featureProjection: "EPSG:3857",
};

// ── Field Shape Preview (Canvas-based mini thumbnail) ──────────────
const FieldShapePreview: React.FC<{ geometry: any; color?: string }> = ({ geometry, color }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !geometry?.coordinates) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const size = 40;
    const pad = 4;
    canvas.width = size;
    canvas.height = size;
    ctx.clearRect(0, 0, size, size);

    // Flatten all coords from the polygon
    const coords: [number, number][] = [];
    const rings = geometry.type === "MultiPolygon"
      ? geometry.coordinates.flatMap((p: any) => p)
      : geometry.coordinates;
    rings.forEach((ring: any) => {
      ring.forEach((pt: any) => coords.push([pt[0], pt[1]]));
    });

    if (coords.length < 3) return;

    const lngs = coords.map((c) => c[0]);
    const lats = coords.map((c) => c[1]);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    const dLng = maxLng - minLng || 1e-6;
    const dLat = maxLat - minLat || 1e-6;
    const drawSize = size - pad * 2;
    const scale = Math.min(drawSize / dLng, drawSize / dLat);
    const offX = pad + (drawSize - dLng * scale) / 2;
    const offY = pad + (drawSize - dLat * scale) / 2;

    const toX = (lng: number) => offX + (lng - minLng) * scale;
    const toY = (lat: number) => offY + (maxLat - lat) * scale; // flip Y

    const fillColor = color || "#68ad18";
    ctx.fillStyle = hexToRgba(fillColor, 0.35);
    ctx.strokeStyle = fillColor;
    ctx.lineWidth = 1.5;

    const drawRing = (ring: [number, number][]) => {
      ctx.beginPath();
      ring.forEach(([lng, lat], i) => {
        if (i === 0) ctx.moveTo(toX(lng), toY(lat));
        else ctx.lineTo(toX(lng), toY(lat));
      });
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    };

    if (geometry.type === "MultiPolygon") {
      geometry.coordinates.forEach((poly: any) =>
        poly.forEach((ring: any) => drawRing(ring))
      );
    } else {
      geometry.coordinates.forEach((ring: any) => drawRing(ring));
    }
  }, [geometry, color]);

  if (!geometry?.coordinates) return <FieldPreviewWrap />;

  return (
    <FieldPreviewWrap>
      <canvas ref={canvasRef} style={{ width: 40, height: 40 }} />
    </FieldPreviewWrap>
  );
};

// ──────────────────────────────────────────────────────────────────────

interface Props {
  userId: string;
}

type ViewMode = "boundary" | "yield";

const UserFieldsSubTab: React.FC<Props> = ({ userId }) => {
  const [fields, setFields] = useState<AdminField[]>([]);
  const [seasons, setSeasons] = useState<AdminSeason[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<string>("");
  const [farms, setFarms] = useState<AdminFarm[]>([]);
  const [selectedFarm, setSelectedFarm] = useState<string>("");
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yieldAggregates, setYieldAggregates] = useState<Record<string, AdminYieldAggregate>>({});
  const [uploading, setUploading] = useState<string | null>(null);

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>("boundary");
  const [yieldLegend, setYieldLegend] = useState<TLegendItem[]>([]);
  const [yieldStats, setYieldStats] = useState<TStatsItem | null>(null);
  const [loadingYield, setLoadingYield] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFieldIdRef = useRef<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OpenLayersMap | null>(null);
  const fieldsSourceRef = useRef(new VectorSource());
  const yieldSourceRef = useRef(new VectorSource());
  const yieldLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const handleFieldClickRef = useRef<(fieldId: string) => void>(() => {});

  // Dropdown data (seasons + farms) — loaded once per user.
  useEffect(() => {
    setLoading(true);
    setError(null);

    const fetchSeasons = getAdminSeasons({ userId })
      .then((data) => {
        setSeasons(data.seasons);
        const current = data.seasons.find((s) => s.isCurrent);
        if (current) setSelectedSeason(current.id);
      })
      .catch((e) => {
        console.error("Failed to fetch seasons:", e);
      });

    const fetchFarms = getAdminFarms({ userId })
      .then((data) => {
        setFarms(data.farms);
      })
      .catch((e) => {
        console.error("Failed to fetch farms:", e);
      });

    Promise.all([fetchSeasons, fetchFarms]).finally(() => setLoading(false));
  }, [userId]);

  // Fields — re-fetched whenever the selected farm changes (empty = all farms).
  useEffect(() => {
    getAdminFields({ userId, farmId: selectedFarm || undefined, limit: 500 })
      .then((data) => {
        setFields(data.fields);
        setError(null);
      })
      .catch((e) => {
        console.error("Failed to fetch fields:", e);
        setError(`Failed to load fields: ${e.message}`);
      });
  }, [userId, selectedFarm]);

  // While this is true the component renders the full-panel empty state and
  // the map container is NOT in the DOM (fields load in their own effect and
  // can arrive after the loading gate opens).
  const showEmptyState = fields.length === 0 && !selectedFarm;

  // Initialize map (runs when loading completes and MapContainer is in DOM).
  // Must also re-run when showEmptyState flips off — that's the moment the
  // map container first mounts if fields arrived after loading finished.
  useEffect(() => {
    if (loading || showEmptyState || !mapContainerRef.current) return;

    const tileUrl = process.env.REACT_APP_LOW_RES_SATELITE_API_URL;

    const yieldLayer = new VectorLayer({
      source: yieldSourceRef.current,
      style: (feature) => {
        const fill = (feature as Feature<Geometry>).get("fill") || "rgba(0,128,0,0.5)";
        const stroke = (feature as Feature<Geometry>).get("stroke") || "#333";
        return new Style({
          fill: new Fill({ color: fill }),
          stroke: new Stroke({ color: stroke, width: 0.5 }),
        });
      },
      visible: false,
    });
    yieldLayerRef.current = yieldLayer;

    const map = new OpenLayersMap({
      layers: [
        new TileLayer({
          source: tileUrl
            ? new TileJSON({
                url: tileUrl,
                tileSize: 512,
                crossOrigin: "anonymous",
              })
            : undefined,
        }),
        new VectorLayer({
          source: fieldsSourceRef.current,
          style: (feature) => {
            const color = (feature as Feature<Geometry>).get("fieldColor") || "#68ad18";
            const selected = (feature as Feature<Geometry>).get("selected");
            return new Style({
              stroke: new Stroke({
                color: selected ? "#ffffff" : color,
                width: selected ? 3 : 2,
              }),
              fill: new Fill({
                color: selected
                  ? "rgba(104,173,24,0.4)"
                  : hexToRgba(color, 0.25),
              }),
            });
          },
        }),
        yieldLayer,
      ],
      controls: [],
      // A View without a center/zoom renders nothing at all (blank panel),
      // so always start from a sane default even before fields arrive.
      view: new View({ center: [0, 0], zoom: 2, maxZoom: 22 }),
    });

    // Click handler for field polygons on the map
    map.on("singleclick", (evt: MapBrowserEvent<UIEvent>) => {
      const features = map.getFeaturesAtPixel(evt.pixel);
      if (features && features.length > 0) {
        // Find the first feature that has a fieldId (from boundary layer)
        const fieldFeature = features.find((f) => (f as Feature<Geometry>).get("fieldId"));
        if (fieldFeature) {
          const fieldId = (fieldFeature as Feature<Geometry>).get("fieldId");
          if (fieldId) {
            handleFieldClickRef.current(fieldId);
          }
        }
      }
    });

    // Change cursor on hover over field polygons
    map.on("pointermove", (evt) => {
      const hit = map.hasFeatureAtPixel(evt.pixel);
      const target = map.getTargetElement();
      if (target) {
        (target as HTMLElement).style.cursor = hit ? "pointer" : "";
      }
    });

    map.setTarget(mapContainerRef.current);
    mapRef.current = map;

    // Fields are fetched in a separate effect and may have landed before the
    // map existed — fit to whatever is already in the source.
    const preloadedExtent = fieldsSourceRef.current.getExtent();
    if (
      fieldsSourceRef.current.getFeatures().length > 0 &&
      !isEmptyExtent(preloadedExtent)
    ) {
      map.getView().fit(preloadedExtent, { padding: [40, 40, 40, 40], maxZoom: 17 });
    }

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
      yieldLayerRef.current = null;
    };
  }, [loading, showEmptyState]);

  // Load field features onto map (when fields data changes)
  useEffect(() => {
    const source = fieldsSourceRef.current;
    source.clear();

    if (fields.length === 0) return;

    const format = new GeoJSON();

    fields.forEach((field) => {
      if (!field.geometry) return;

      const geojson = {
        type: "Feature" as const,
        geometry: field.geometry,
        properties: {
          id: field.id,
          name: field.name,
          fieldColor: field.color || "#68ad18",
        },
      };

      try {
        const features = format.readFeatures(geojson, optOptions);
        features.forEach((f) => {
          f.set("fieldId", field.id);
          f.set("fieldColor", field.color || "#68ad18");
          f.set("selected", false);
        });
        source.addFeatures(features as Feature<Geometry>[]);
      } catch (e) {
        console.warn(`Failed to parse geometry for field ${field.name}:`, e);
      }
    });

    // Fit to all fields extent on initial load
    if (source.getFeatures().length > 0 && mapRef.current) {
      const extent = source.getExtent();
      if (!isEmptyExtent(extent)) {
        mapRef.current.getView().fit(extent, { padding: [40, 40, 40, 40], maxZoom: 17 });
      }
    }
  }, [fields]);

  // Update selected state on features when selection changes
  useEffect(() => {
    const source = fieldsSourceRef.current;
    source.getFeatures().forEach((f) => {
      f.set("selected", f.get("fieldId") === selectedFieldId);
    });
  }, [selectedFieldId]);

  // Fetch yield aggregates for all fields when season changes
  useEffect(() => {
    if (!selectedSeason || fields.length === 0) {
      setYieldAggregates({});
      return;
    }

    let cancelled = false;
    getFieldYieldAggregates(fields.map((f) => f.id), selectedSeason)
      .then((agg) => {
        if (!cancelled) setYieldAggregates(agg);
      })
      .catch(() => {
        if (!cancelled) setYieldAggregates({});
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSeason, fields]);

  // Toggle yield layer visibility based on view mode
  useEffect(() => {
    if (yieldLayerRef.current) {
      yieldLayerRef.current.setVisible(viewMode === "yield");
    }
  }, [viewMode]);

  // Load yield heatmap when switching to yield view or when selected field/season changes
  useEffect(() => {
    if (viewMode !== "yield" || !selectedFieldId || !selectedSeason) {
      yieldSourceRef.current.clear();
      setYieldLegend([]);
      setYieldStats(null);
      return;
    }

    setLoadingYield(true);
    getHeatmapData(selectedFieldId, selectedSeason)
      .then((res) => {
        const source = yieldSourceRef.current;
        source.clear();

        if (res.features?.features?.length) {
          const format = new GeoJSON();
          const features = format.readFeatures(res.features, optOptions);
          source.addFeatures(features as Feature<Geometry>[]);
        }

        setYieldLegend(res.legend || []);
        setYieldStats(res.stats || null);
      })
      .catch((err) => {
        console.error("Failed to load yield heatmap:", err);
        setYieldLegend([]);
        setYieldStats(null);
      })
      .finally(() => setLoadingYield(false));
  }, [viewMode, selectedFieldId, selectedSeason]);

  const handleUploadClick = (fieldId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedSeason) {
      alert("Please select a season first");
      return;
    }
    uploadFieldIdRef.current = fieldId;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const fieldId = uploadFieldIdRef.current;
    if (!file || !fieldId || !selectedSeason) return;

    // Reset input so the same file can be re-selected
    e.target.value = "";

    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert("Please select a CSV file");
      return;
    }

    setUploading(fieldId);
    try {
      const result = await uploadFieldYield(fieldId, selectedSeason, file);
      const fieldName = fields.find((f) => f.id === fieldId)?.name ?? fieldId;
      const msg = `Imported ${result.imported} records for "${fieldName}".\nAvg: ${result.aggregate.avg.toFixed(1)} c/ha`;
      if (result.skipped > 0) {
        alert(`${msg}\n${result.skipped} rows skipped.`);
      } else {
        alert(msg);
      }
      // Refresh aggregate for this field
      const fresh = (await getFieldYieldAggregates([fieldId], selectedSeason))[fieldId];
      if (fresh) {
        setYieldAggregates((prev) => ({ ...prev, [fieldId]: fresh }));
      }
    } catch (err: any) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(null);
    }
  };

  // Fields with yield data for the selected season sort to the top
  // (stable sort — original order is kept within each group).
  const sortedFields = useMemo(() => {
    return [...fields].sort(
      (a, b) =>
        (yieldAggregates[b.id] ? 1 : 0) - (yieldAggregates[a.id] ? 1 : 0)
    );
  }, [fields, yieldAggregates]);

  const handleFieldClick = useCallback((fieldId: string) => {
    setSelectedFieldId((prev) => {
      const newId = fieldId === prev ? null : fieldId;

      // Zoom to field
      if (newId && mapRef.current) {
        const features = fieldsSourceRef.current
          .getFeatures()
          .filter((f) => f.get("fieldId") === newId);
        if (features.length > 0) {
          const extent = features[0].getGeometry()?.getExtent();
          if (extent != null && !isEmptyExtent(extent)) {
            mapRef.current
              .getView()
              .fit(extent, { padding: [60, 60, 60, 60], maxZoom: 18, duration: 500 });
          }
        }
      }

      // Reset to boundary view when deselecting
      if (!newId) {
        setViewMode("boundary");
      }

      return newId;
    });
  }, []);

  // Keep ref in sync for the map click handler
  handleFieldClickRef.current = handleFieldClick;

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  // Only take over the whole panel when the user truly has no fields (no farm
  // filter active). When a specific farm is selected but empty, keep the panel
  // and its selectors visible so the admin can switch back.
  if (showEmptyState) {
    return (
      <EmptyState>
        <Text size={14} color={error ? "#e74c3c" : "#899093"}>
          {error || "This user has no fields."}
        </Text>
      </EmptyState>
    );
  }

  return (
    <Container>
      <LeftPanel>
        {seasons.length > 0 && (
          <SeasonRow>
            <Text size={12} color="#899093">
              Season:
            </Text>
            <SeasonSelect
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              <option value="">All seasons</option>
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </SeasonSelect>
          </SeasonRow>
        )}

        {farms.length > 0 && (
          <SeasonRow>
            <Text size={12} color="#899093">
              Farm:
            </Text>
            <SeasonSelect
              value={selectedFarm}
              onChange={(e) => setSelectedFarm(e.target.value)}
            >
              <option value="">All farms</option>
              {farms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </SeasonSelect>
          </SeasonRow>
        )}

        <FieldCount>
          <Text size={12} color="#899093">
            {fields.length} fields
          </Text>
        </FieldCount>

        {fields.length === 0 && (
          <EmptyState>
            <Text size={13} color={error ? "#e74c3c" : "#899093"}>
              {error || "No fields in this farm."}
            </Text>
          </EmptyState>
        )}

        {sortedFields.map((field) => {
          const agg = yieldAggregates[field.id];
          const isUploading = uploading === field.id;
          return (
            <FieldCard
              key={field.id}
              selected={field.id === selectedFieldId}
              onClick={() => handleFieldClick(field.id)}
            >
              <FieldDetail>
                <FieldShapePreview geometry={field.geometry} color={field.color || undefined} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FieldName>{field.name}</FieldName>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Text size={12} color="#899093">
                      {field.areaHectares
                        ? `${field.areaHectares.toFixed(1)} ha`
                        : "No area"}
                    </Text>
                    {selectedSeason && (
                      <UploadButton
                        disabled={isUploading}
                        onClick={(e) => handleUploadClick(field.id, e)}
                      >
                        {isUploading ? "Uploading..." : "Upload Yield"}
                      </UploadButton>
                    )}
                  </div>
                </div>
              </FieldDetail>
              {agg && (
                <YieldInfo>
                  <Text size={11} color="#68ad18">
                    Yield: avg {agg.avg.toFixed(1)} c/ha | min {agg.min.toFixed(1)} | max{" "}
                    {agg.max.toFixed(1)} | {agg.count} pts
                  </Text>
                </YieldInfo>
              )}
            </FieldCard>
          );
        })}
      </LeftPanel>

      <RightPanel>
        <MapContainer ref={mapContainerRef} />
        {selectedFieldId && selectedSeason && (
          <MapOverlay>
            <ViewSelect
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as ViewMode)}
            >
              <option value="boundary">Field boundary</option>
              <option value="yield">Yield</option>
            </ViewSelect>

            {viewMode === "yield" && loadingYield && (
              <LegendBox>
                <LegendTitle>Loading yield data...</LegendTitle>
              </LegendBox>
            )}

            {viewMode === "yield" && !loadingYield && yieldLegend.length > 0 && (
              <LegendBox>
                <LegendTitle>Yield (c/ha)</LegendTitle>
                {yieldLegend.map((item, i) => (
                  <LegendRow key={i}>
                    <LegendSwatch color={item.color} />
                    <LegendLabel>{item.range}</LegendLabel>
                  </LegendRow>
                ))}
                {yieldStats && (
                  <StatsRow>
                    Avg: {yieldStats.avg} c/ha&nbsp;&nbsp;Total: {yieldStats.total}
                  </StatsRow>
                )}
              </LegendBox>
            )}

            {viewMode === "yield" && !loadingYield && yieldLegend.length === 0 && (
              <LegendBox>
                <LegendTitle style={{ color: "#899093", fontWeight: "normal" }}>
                  No yield data available
                </LegendTitle>
              </LegendBox>
            )}
          </MapOverlay>
        )}
      </RightPanel>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
      />
    </Container>
  );
};

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(104,173,24,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

export default UserFieldsSubTab;
